import { createSelector } from '@ngrx/store';

import { AGENCY_BALANCES_PATH, AGENCY_CORRECTIONS_PATH } from '@ln/shared-route-segments-util';
import { AGENCY_ADJUSTMENTS_AUTH_GROUPS } from '@ln/timesheet-adjustments-util';
import { IMicroAppListItemState } from '@locumsnest/core/src/lib/micro-app/interfaces';
import { selectMicroAppNavigation } from '@locumsnest/core/src/lib/micro-app/micro-app.reducer';

import { selectAuthGroupNames } from '../../../auth-group/+state/auth-group.selectors';
import { AUTH_GROUPS } from '../../../auth-group/constants';
import { PRODUCT_CODES } from '../../constants';

type AuthGroupMatchPolicy = 'any' | 'all';

interface AuthGroupAccessRequirement {
  requiredAuthGroups: string[];
  matchPolicy: AuthGroupMatchPolicy;
}

// Keyed by product code for the switcher, or by nav-item path for a single side-nav entry.
const NAV_ACCESS_REQUIREMENTS: Record<string, AuthGroupAccessRequirement> = {
  [PRODUCT_CODES.CONTROL]: {
    requiredAuthGroups: [
      AUTH_GROUPS.CONTROL_ADMIN_USER,
      AUTH_GROUPS.CONTROL_INTERMEDIATE,
      AUTH_GROUPS.CONTROL_READ_ONLY,
    ],
    matchPolicy: 'any',
  },
  [PRODUCT_CODES.CONTROL_ADMIN]: {
    requiredAuthGroups: [AUTH_GROUPS.LOCUMS_CONTROL_ADMIN, AUTH_GROUPS.CUSTOMER_ADMIN],
    matchPolicy: 'any',
  },
  [PRODUCT_CODES.MAINTENANCE]: {
    requiredAuthGroups: [AUTH_GROUPS.BANK_RATES_MANAGER],
    matchPolicy: 'any',
  },
  [PRODUCT_CODES.EXCEPTION_REPORTING]: {
    requiredAuthGroups: [
      AUTH_GROUPS.GUARDIAN_OF_SAFE_WORKING_HOURS,
      AUTH_GROUPS.DIRECTOR_OF_MEDICAL_EDUCATION,
      AUTH_GROUPS.HR_MEDICAL_STAFFING_ACCESS,
    ],
    matchPolicy: 'any',
  },
  [PRODUCT_CODES.PAYROLL_EXPORTS]: {
    requiredAuthGroups: [AUTH_GROUPS.PAYROLL_OFFICER],
    matchPolicy: 'any',
  },
  [PRODUCT_CODES.PAYROLL_ADJUSTMENTS]: {
    requiredAuthGroups: [
      AUTH_GROUPS.PAYROLL_ADJUSTMENTS_OFFICER,
      AUTH_GROUPS.PAYROLL_ADJUSTMENT_APPROVER,
    ],
    matchPolicy: 'any',
  },
  [AGENCY_CORRECTIONS_PATH]: {
    requiredAuthGroups: AGENCY_ADJUSTMENTS_AUTH_GROUPS,
    matchPolicy: 'any',
  },
  [AGENCY_BALANCES_PATH]: {
    requiredAuthGroups: AGENCY_ADJUSTMENTS_AUTH_GROUPS,
    matchPolicy: 'any',
  },
};

const officerSatisfiesAccessRequirement = (key: string, officerAuthGroups: string[]) => {
  const accessRequirement = NAV_ACCESS_REQUIREMENTS[key];

  if (!accessRequirement) {
    return true;
  }

  const { requiredAuthGroups, matchPolicy } = accessRequirement;

  if (matchPolicy === 'all') {
    return requiredAuthGroups.every((authGroup) => officerAuthGroups.includes(authGroup));
  }

  return requiredAuthGroups.some((authGroup) => officerAuthGroups.includes(authGroup));
};

export const filterMicroAppListForOfficerAuthGroups = (
  list: IMicroAppListItemState[],
  officerAuthGroups: string[],
) =>
  list.filter(
    ({ code }) =>
      code !== PRODUCT_CODES.AGENCY && officerSatisfiesAccessRequirement(code, officerAuthGroups),
  );

export const filterNavItemsForOfficerAuthGroups = <T extends { path: string }>(
  navItems: T[],
  officerAuthGroups: string[],
): T[] => navItems.filter(({ path }) => officerSatisfiesAccessRequirement(path, officerAuthGroups));

export const selectVisibleNavItems = createSelector(
  selectMicroAppNavigation,
  selectAuthGroupNames,
  filterNavItemsForOfficerAuthGroups,
);
